import { useMemo } from "react";
import { fromTimestamp } from "@racoon/lib";
import { DateValue } from "~/shared/lib/analytics-utils";
import { byDateThenPickupLocation, containerVolume } from "./job-report";
import { useCompletedJobs } from "./use-report-data";
import { useReportFilterValues } from "./use-report-filters";

/** One row of «Детальный отчёт по вывозу» (AIS 15.1.2): a completed job. */
export interface CollectionReportRow {
  jobId: bigint;
  factEndAt: Date | null;
  serviceReceiverId: bigint | null;
  serviceReceiverName: string;
  serviceReceiverBin: string;
  pickupLocationId: bigint | null;
  pickupLocationName: string;
  containerTypeId: string;
  containerTypeName: string;
  numberOfContainers: number | null;
  containerVolume: number | null;
  factTotalVolume: number | null;
  truckId: bigint | null;
  truckPlateNumber: string;
  driverId: bigint | null;
  driverName: string;
  loaderNames: string;
  contractDealNumber: string;
}

/** Completed jobs, dated by completion. Container type narrows the loaded rows. */
export function useCollectionReportRows(startDate: DateValue, endDate: DateValue) {
  const { serviceReceiver, pickupLocation, containerType, driver, truck } = useReportFilterValues();
  const { rows, isLoading, error } = useCompletedJobs(
    startDate,
    endDate,
    { serviceReceiver, pickupLocation, driver, truck },
    { withLoaders: true, withContracts: true },
  );

  const collectionRows = useMemo(
    () =>
      rows
        .map(
          (job): CollectionReportRow => ({
            jobId: job.id,
            factEndAt: fromTimestamp(job.factEndAt),
            serviceReceiverId: job.serviceReceiverId,
            serviceReceiverName: job.serviceReceiverName ?? "",
            serviceReceiverBin: job.serviceReceiverBin ?? "",
            pickupLocationId: job.pickupLocationId,
            pickupLocationName: job.pickupLocationName ?? "",
            containerTypeId: job.containerTypeId ? String(job.containerTypeId) : "",
            containerTypeName: job.containerTypeName ?? "",
            // Serviced, as the photo report counts them; the nominal volume stays the plan's.
            numberOfContainers: job.factContainerCount ?? null,
            containerVolume: containerVolume(job),
            factTotalVolume: job.factTotalVolume ?? null,
            truckId: job.truckId ?? null,
            truckPlateNumber: job.truckPlateNumber ?? "",
            driverId: job.driverId ?? null,
            driverName: job.driverName ?? "",
            loaderNames: job.loaderNames ?? "",
            contractDealNumber: job.contractDealNumber ?? "",
          }),
        )
        .filter((row) => !containerType || row.containerTypeId === containerType)
        .sort(byDateThenPickupLocation((row) => row.factEndAt)),
    [rows, containerType],
  );

  return { rows: collectionRows, isLoading, error };
}
